import React, { useState, useEffect } from "react";
import { Trash2, Edit2, Save } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { DatePicker } from "rsuite";
import { BsCalendar2MonthFill } from "react-icons/bs";
import BatchSelectModal from "../pharmacy/pharmacymodels/BatchSelectModal";

const formatExpiry = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    year: "numeric",
  }); // Example: "Aug 2026"
};



const ProductTable = ({
  products,
  setProducts,
  newProduct,
  setNewProduct,
  onShowProductModal,
  overallDiscountPercent,
}) => {
  console.log("ProductTable Rendered", products, newProduct);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // ✅ Track which row is being edited

  const noArrowsCSS = `
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `;

  const getExpiryStatus = (expiryValue) => {
    if (!expiryValue) return "";
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const [year, month] = expiryValue.split("-").map(Number);
    return year < currentYear || (year === currentYear && month < currentMonth)
      ? "expired"
      : "future";
  };

  const calculateSchemePercentage = (baseScheme, bonusScheme) => {
    const base = parseFloat(baseScheme) || 0;
    const bonus = parseFloat(bonusScheme) || 0;
    if (base === 0) return 0;
    const percent = (bonus / (base + bonus)) * 100;
    return percent.toFixed(2);
  };

  // ✅ Margin Calculation
  const calculateMargin = (
    mrp,
    rate,
    disc,
    gst,
    qty,
    free,
    overallDiscPercent = 0
  ) => {
    const MRP = parseFloat(mrp) || 0;
    const RATE = parseFloat(rate) || 0;
    const DISC = parseFloat(disc) || 0;
    const GST = parseFloat(gst) || 0;
    const OVERALL_DISC = parseFloat(overallDiscPercent) || 0;
    const QTY = parseFloat(qty) || 0;
    const FREE = parseFloat(free) || 0;

    if (MRP <= 0 || RATE <= 0 || QTY <= 0) return 0;
    const effectiveQty = QTY + FREE;
    const schemeAdjustedRate = RATE * (QTY / effectiveQty);

    const totalDiscPercent = DISC + OVERALL_DISC;
    const rateAfterDisc =
      schemeAdjustedRate - (schemeAdjustedRate * totalDiscPercent) / 100;
    const rateWithGst = rateAfterDisc + (rateAfterDisc * GST) / 100;

    const margin = ((MRP - rateWithGst) / MRP) * 100;
    return isFinite(margin) ? parseFloat(margin.toFixed(2)) : 0;
  };

  // ✅ Amount Calculation
  const calculateAmount = (
    qty,
    free,
    rate,
    disc,
    gst,
    overallDiscPercent = 0
  ) => {
    const QTY = parseFloat(qty) || 0;
    const FREE = parseFloat(free) || 0;
    const RATE = parseFloat(rate) || 0;
    const DISC = parseFloat(disc) || 0;
    const GST = parseFloat(gst) || 0;
    const OVERALL_DISC = parseFloat(overallDiscPercent) || 0;

    if (QTY <= 0 || RATE <= 0) return 0;
    const effectiveQty = QTY + FREE;
    const schemeAdjustedRate = RATE * (QTY / effectiveQty);

    const totalDiscPercent = DISC + OVERALL_DISC;
    const rateAfterDisc =
      schemeAdjustedRate - (schemeAdjustedRate * totalDiscPercent) / 100;
    const rateWithGst = rateAfterDisc + (rateAfterDisc * GST) / 100;

    const total = QTY * rateWithGst;
    return isFinite(total) ? parseFloat(total.toFixed(2)) : 0;
  };

  // ✅ Auto update existing product amounts when overall discount changes
  useEffect(() => {
    if (products.length > 0) {
      const updated = products.map((p) => ({
        ...p,
        amount: calculateAmount(
          p.qty,
          p.free,
          p.rate,
          p.disc,
          p.gst,
          overallDiscountPercent
        ),
        margin: calculateMargin(
          p.mrp,
          p.rate,
          p.disc,
          p.gst,
          p.qty,
          p.free,
          overallDiscountPercent
        ),
      }));
      setProducts(updated);
    }
  }, [overallDiscountPercent]);

  const handleNumericChange = (field, value) => {
    if (!/^\d*\.?\d*$/.test(value)) return;
    if (value < 0) return;

    setNewProduct((prev) => ({ ...prev, [field]: value }));

    if (["scheme", "schemeBonus"].includes(field)) {
      const sch = calculateSchemePercentage(
        field === "scheme" ? value : newProduct.scheme,
        field === "schemeBonus" ? value : newProduct.schemeBonus
      );
      setNewProduct((prev) => ({ ...prev, schPercent: sch }));
    }

    const temp = { ...newProduct, [field]: value };
    const margin = calculateMargin(
      temp.mrp,
      temp.rate,
      temp.disc,
      temp.gst,
      temp.qty,
      temp.free,
      overallDiscountPercent
    );
    const amount = calculateAmount(
      temp.qty,
      temp.free,
      temp.rate,
      temp.disc,
      temp.gst,
      overallDiscountPercent
    );
    setNewProduct((prev) => ({
      ...prev,
      margin,
      amount,
    }));
  };

  const handleBatchFieldClick = () => {
    if (!newProduct.productBarcode) {
      toast.error("Please select a product first!", {
        position: "top-center",
        style: { background: "#ef4444", color: "#fff", fontWeight: 500 },
      });
      return;
    }
    setShowBatchModal(true);
  };

  const handleBatchSelect = (batch) => {
    // Format expiry date to 'MMM/YYYY' (e.g., Feb/2027)
    let formattedExpiry = "";
    if (batch.expiry) {
      const date = new Date(batch.expiry);
      formattedExpiry = date.toLocaleString("en-US", {
        month: "short",
        year: "numeric",
      });
    }

    setNewProduct((prev) => ({
      ...prev,
      batchId: batch.batchId || batch.batchNo,
      batch: batch.batchNo,
      expiry: formattedExpiry,
      mrp: batch.mrp || "",
      hsn: batch.hsnCode || "",
      gst: batch.gst || "",
      rate: batch.purcRate || "",
    }));

    setShowBatchModal(false);
  };

const addProduct = () => {
  if (!newProduct.productBarcode.trim()) {
    toast.error("Please select a product first!", {
      position: "top-center",
      style: { background: "#ef4444", color: "#fff", fontWeight: 500 },
    });
    return;
  }

  if (!newProduct.qty || parseFloat(newProduct.qty) <= 0) {
    toast.error("Please enter a valid quantity!", {
      position: "top-center",
      style: { background: "#ef4444", color: "#fff", fontWeight: 500 },
    });
    return;
  }

  const newProd = {
    ...newProduct,
    qty: parseFloat(newProduct.qty) || 0,
    rate: parseFloat(newProduct.rate) || 0,
    mrp: parseFloat(newProduct.mrp) || 0,
    disc: parseFloat(newProduct.disc) || 0,
    gst: parseFloat(newProduct.gst) || 0,
    schPercent: calculateSchemePercentage(
      newProduct.scheme,
      newProduct.schemeBonus
    ),
  };

  newProd.margin = calculateMargin(
    newProd.mrp,
    newProd.rate,
    newProd.disc,
    newProd.gst,
    newProd.qty,
    newProd.free,
    overallDiscountPercent
  );

  newProd.amount = calculateAmount(
    newProd.qty,
    newProd.free,
    newProd.rate,
    newProd.disc,
    newProd.gst,
    overallDiscountPercent
  );

  if (editIndex !== null) {
    const updated = [...products];
    updated[editIndex] = { ...updated[editIndex], ...newProd };
    setProducts(updated);
    setEditIndex(null);
    toast.success("Product updated successfully!");
  } else {
    setProducts([...products, { ...newProd, id: Date.now() }]);
    toast.success("Product added!");
  }

  setNewProduct({
    productBarcode: "",
    hsn: "",
    batch: "",
    expiry: "",
    pack: "1 x",
    qty: "",
    free: "",
    mrp: "",
    rate: "",
    scheme: "",
    schemeBonus: "",
    schPercent: "",
    disc: "",
    gst: "",
    margin: "",
    amount: 0,
  });
};


  const editProduct = (i) => {
    setNewProduct(products[i]);
    setEditIndex(i);
    toast("Edit mode enabled!", {
      icon: "✏️",
      position: "top-center",
      style: { background: "#3b82f6", color: "#fff", fontWeight: 500 },
    });
  };

  const deleteProduct = (i) => {
    setProducts(products.filter((_, x) => x !== i));
  };

  const percentField = (
    value,
    readOnly = false,
    field = "",
    onChange = () => {}
  ) => (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        inputMode="decimal"
        readOnly={readOnly}
        value={value || ""}
        onChange={(e) => onChange(field, e.target.value)}
        className="table-input"
        style={{
          width: "70px",
          textAlign: "right",
          background: readOnly ? "#f9fafb" : "white",
          fontWeight: 600,
          paddingRight: "18px",
        }}
      />
      <span
        style={{
          position: "absolute",
          right: "5px",
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: "13px",
          color: "#666",
        }}
      >
        %
      </span>
    </div>
  );

  return (
    <>
      <style>{noArrowsCSS}</style>
      <Toaster />
      <div className="products-table">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>PRODUCT</th>
              <th>HSN</th>
              <th>BATCH</th>
              <th>EXPIRY</th>
              <th>PACK</th>
              <th>QTY</th>
              <th>FREE</th>
              <th>MRP</th>
              <th>RATE</th>
              <th>SCHEME</th>
              <th>SCH %</th>
              <th>DISC</th>
              <th>GST</th>
              <th>MARGIN</th>
              <th>AMT</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {/* Add/Edit Row */}
            <tr className="new-product-row">
              <td></td>
              <td>
                <input
                  type="text"
                  placeholder="Type or Press Space"
                  value={newProduct.productBarcode}
                  onFocus={onShowProductModal}
                  readOnly
                  className="table-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={newProduct.hsn}
                  onChange={(e) =>
                    setNewProduct((p) => ({ ...p, hsn: e.target.value }))
                  }
                  className="table-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={newProduct.batch}
                  onClick={handleBatchFieldClick}
                  readOnly
                  className="table-input"
                  placeholder="Click to select"
                />
              </td>
              <td>
                <div style={{ position: "relative" }}>
                  <DatePicker
                    format="MMM yyyy"
                    caretAs={BsCalendar2MonthFill}
                    value={
                      newProduct.expiry ? new Date(newProduct.expiry) : null
                    }
                    onChange={(date) => {
                      if (!date) return;
                      const backendFormat = date.toISOString().split("T")[0];
                      setNewProduct((p) => ({ ...p, expiry: backendFormat }));
                    }}
                    style={{
                      width: "120px",
                      fontSize: "12px",
                      borderRadius: "6px",
                      padding: "0px 0px",
                      color: "#111827",
                    }}
                    placement="autoVerticalStart"
                    cleanable={false}
                    editable={false}
                    placeholder="Select Month"
                    appearance="subtle"
                    oneTap
                    className="custom-datepicker"
                  />
                </div>

                {/* Inline custom focus style */}
                <style>
                  {`
      .custom-datepicker .rs-input {
        border: none !important;
        font-size: 12px !important;
        color: #111827 !important;
      }

      .custom-datepicker .rs-picker-toggle {
        border: 1px solid #d1d5db !important;
        font-size: 12px !important;
        height: 32px !important;
      }

      .custom-datepicker .rs-picker-toggle:hover,
      .custom-datepicker .rs-picker-toggle:focus,
      .custom-datepicker .rs-picker-toggle:active {
        border-color: #10b981 !important; /* focus color same as other inputs */
        box-shadow: 0 0 0 1px #10b98133 !important;
      }

      .custom-datepicker .rs-picker-toggle-placeholder {
        color: #6b7280 !important; /* gray placeholder */
        font-size: 12px !important;
      }

      .custom-datepicker .rs-picker-toggle-value {
        font-size: 12px !important;
        color: #111827 !important;
        font-weight: 500 !important;
      }

      .custom-datepicker .rs-picker-toggle-caret {
        color: #6b7280 !important;
      }
    `}
                </style>
              </td>

              <td>
                <input
                  type="text"
                  value={newProduct.pack}
                  onChange={(e) =>
                    setNewProduct((p) => ({ ...p, pack: e.target.value }))
                  }
                  className="table-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newProduct.qty || ""}
                  onChange={(e) => handleNumericChange("qty", e.target.value)}
                  className="table-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newProduct.free || ""}
                  onChange={(e) => handleNumericChange("free", e.target.value)}
                  className="table-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newProduct.mrp || ""}
                  onChange={(e) => handleNumericChange("mrp", e.target.value)}
                  className="table-input"
                />
              </td>
              <td>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newProduct.rate || ""}
                  onChange={(e) => handleNumericChange("rate", e.target.value)}
                  className="table-input"
                />
              </td>
              <td>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <input
                    type="text"
                    inputMode="decimal"
                    value={newProduct.scheme || ""}
                    onChange={(e) =>
                      handleNumericChange("scheme", e.target.value)
                    }
                    className="table-input"
                    style={{ width: "45px", textAlign: "center" }}
                  />
                  <span style={{ fontWeight: 600 }}>+</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={newProduct.schemeBonus || ""}
                    onChange={(e) =>
                      handleNumericChange("schemeBonus", e.target.value)
                    }
                    className="table-input"
                    style={{ width: "45px", textAlign: "center" }}
                  />
                </div>
              </td>
              <td>{percentField(newProduct.schPercent, true)}</td>
              <td>
                {percentField(
                  newProduct.disc,
                  false,
                  "disc",
                  handleNumericChange
                )}
              </td>
              <td>
                {percentField(
                  newProduct.gst,
                  false,
                  "gst",
                  handleNumericChange
                )}
              </td>
              <td>{percentField(newProduct.margin, true)}</td>
              <td>₹ {newProduct.amount || 0}</td>
              <td>
               <button type="button" className="gradient-button" onClick={addProduct}>
                  {editIndex !== null ? "UPDATE" : "ADD"}
                </button>
              </td>
            </tr>

            {/* Existing Product Rows */}
            {products.map((p, i) => (
              <tr key={p.id}>
                <td>{i + 1}</td>
                <td>{p.productBarcode}</td>
                <td>{p.hsn}</td>
                <td>{p.batchId}</td>
                <td>{formatExpiry(p.expiry)}</td>
                <td>{p.pack}</td>
                <td>{p.qty}</td>
                <td>{p.free}</td>
                <td>{p.mrp}</td>
                <td>{p.rate}</td>
                <td>
                  {p.scheme}+{p.schemeBonus}
                </td>
                <td>{p.schPercent}%</td>
                <td>{p.disc}%</td>
                <td>{p.gst}%</td>
                <td
                  style={{
                    color: parseFloat(p.margin) < 0 ? "#ef4444" : "#10b981",
                    fontWeight: 600,
                  }}
                >
                  {p.margin}%
                </td>
                <td>₹ {p.amount}</td>
                <td style={{ display: "flex", gap: "10px" }}>
                  <Edit2
                    size={20}
                    style={{
                      marginRight: 10,
                      cursor: "pointer",
                      color: "#464646ff",
                    }}
                    onClick={() => editProduct(i)}
                  />
                  <Trash2
                    size={20}
                    className="delete-icon"
                    onClick={() => deleteProduct(i)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BatchSelectModal
        open={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onSelect={handleBatchSelect}
        productId={newProduct.productId}
        productName={newProduct.productBarcode}
      />
    </>
  );
};

export default ProductTable;
